import axios from 'axios';

let isDev = false;
let API_BASE_URL = '';

if (isDev) {
  API_BASE_URL =
    'https://journalism-dial-roland-salvador.trycloudflare.com/api' ||
    'http://localhost:5000/api';
} else {
  API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    'https://insights-backend-2jfw.onrender.com/api';
}

export const getAuthToken = () => {
  return localStorage.getItem('token');
};

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};

export const removeAuthToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    delete config.skipAuth;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // For blob responses (file downloads), return full response to access headers
    if (response.config.responseType === 'blob') {
      return response;
    }
    
    if (response.data && response.data.success === false) {
      throw new Error(response.data.message || 'API call failed');
    }

    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      removeAuthToken();
      window.location.href = '/login';
      return Promise.reject(new Error('Session expired. Please login again.'));
    }

    if (error.response?.status === 403) {
      return Promise.reject(
        new Error('We haven’t received your consent yet, or it’s still awaiting approval.')
      );
    }

    if (error.response?.status === 404) {
      return Promise.reject(new Error('Invalid email address: This email does not exist in our records.'));
    }

    if (error.response?.status === 500) {
      return Promise.reject(new Error('Server error. Please try again later.'));
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject(
        new Error('Request timeout. Please check your connection.')
      );
    }

    if (!error.response) {
      return Promise.reject(
        new Error('Network error. Please check if backend is running.')
      );
    }

    const errorMessage =
      error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(new Error(errorMessage));
  }
);

export const API_ENDPOINTS = {
  SEND_OTP: '/auth/send-otp',
  VERIFY_OTP: '/auth/verify-otp',
  RESEND_OTP: '/auth/resend-otp',
  CHECK_EMAIL: '/auth/check-email',
  CHECK_CONSENT: '/consent/verify',
  LOGOUT: '/auth/logout',
  PROFILE: '/auth/profile',
  LOGIN_HISTORY: '/auth/login-history',

  USERS_LIST: '/users/list',
  USER_BY_ID: (id) => `/users/${id}`,
  USER_CREATE: '/users/create',
  USER_UPDATE: (id) => `/users/${id}`,
  USER_DELETE: (id) => `/users/${id}`,
  USER_HISTORY: (id) => `/users/${id}/history`,
  BULK_IMPORT: '/users/bulk-import',
  USERS_BY_ROLE: (roleId) => `/users/by-role/${roleId}`,

  ROLES_LIST: '/roles/list',
  ROLE_BY_ID: (id) => `/roles/${id}`,
  ROLE_CREATE: '/roles/create',
  ROLE_UPDATE: (id) => `/roles/${id}`,
  ROLE_DELETE: (id) => `/roles/${id}`,
  ROLE_USERS: (id) => `/roles/${id}/users`,
  ROLE_HISTORY: (id) => `/roles/${id}/history`,
  ROLES_DROPDOWN: '/roles/dropdown/list',

  SURVEY_GET_STUDY: `/survey/study`,
  SURVEY_SUBMIT: '/survey/submit-survey',
  SURVEY_SAVE_DRAFT: '/survey/save-draft',
  SURVEY_USER_RESPONSES: (userId, studyId, subjectId) =>
    `/survey/user-responses/${userId}/${studyId}${subjectId ? `/${subjectId}` : ''}`,
  SURVEY_HEALTH: '/survey/health',
  AUDIT_GET_LOGS: '/audit/get-audit-logs',
  AUDIT_GET_DETAILS: '/audit/get-audit-details',
  AUDIT_DOWNLOAD: '/audit/download',
  STUDIES_STATS: '/studies/stats',
  STUDIES_RECENT_SUBMISSIONS: '/studies/recent-submissions',
  SUBJECT_STATS: '/subject/stats',
  STUDIES_DROPDOWN: '/studies/dropdown/list',
  SITES_DROPDOWN: '/sites/dropdown/list',
  CREATE_SUBJECT: '/subject/create',
  GET_SUBJECTS: '/subject/list',
  SUBJECT_DELETE: '/subject',
  DOWNLOAD_FORM: 'consent/download',

  UPLOAD: 'files/uploads',
  DELETE: 'files/delete',
  GET_CONSENT_FILES: 'files/get-files',

  CONSENT: 'consent/load-consent',
  CONSENT_CREATE: 'consent/create',
  // GET_FILES: '/get-files',
  GET_CONSENT: '/consent/get-consent',
  UPDATDE_STATUS: '/consent/update-status',
  USERS_EXPANDED: '/consent/dropdown/list',
  GET_CREATED_SUBJECTS: '/subject/max-subjects',
};

export const sendOTP = async (email_address) => {
  return axiosInstance.post(
    API_ENDPOINTS.SEND_OTP,
    { email_address },
    { skipAuth: true }
  );
};

export const verifyOTP = async (email_address, otp) => {
  return axiosInstance.post(
    API_ENDPOINTS.VERIFY_OTP,
    { email_address, otp },
    { skipAuth: true }
  );
};

export const resendOTP = async (email_address) => {
  return axiosInstance.post(
    API_ENDPOINTS.RESEND_OTP,
    { email_address },
    { skipAuth: true }
  );
};

export const checkEmail = async (email_address) => {
  return axiosInstance.post(
    API_ENDPOINTS.CHECK_EMAIL,
    { email_address },
    { skipAuth: true }
  );
};

export const logout = async () => {
  return axiosInstance.post(API_ENDPOINTS.LOGOUT);
};

export const getProfile = async () => {
  return axiosInstance.get(API_ENDPOINTS.PROFILE);
};

export const updateProfile = async (data) => {
  return axiosInstance.put(API_ENDPOINTS.PROFILE, data);
};

export const getUsersList = async (params) => {
  return axiosInstance.post(API_ENDPOINTS.USERS_LIST, params);
};

export const getUserById = async (id) => {
  return axiosInstance.get(API_ENDPOINTS.USER_BY_ID(id));
};

export const createUser = async (userData) => {
  return axiosInstance.post(API_ENDPOINTS.USER_CREATE, userData);
};

export const updateUser = async (id, userData) => {
  return axiosInstance.put(API_ENDPOINTS.USER_UPDATE(id), userData);
};

export const deleteUser = async (id, deleted_by) => {
  return axiosInstance.delete(API_ENDPOINTS.USER_DELETE(id), {
    data: { deleted_by },
  });
};

export const getUserHistory = async (id, page = 1, limit = 20) => {
  return axiosInstance.get(API_ENDPOINTS.USER_HISTORY(id), {
    params: { page, limit },
  });
};

export const getRolesList = async (params) => {
  return axiosInstance.post(API_ENDPOINTS.ROLES_LIST, params);
};

export const getRolesDropdown = async () => {
  return axiosInstance.get(API_ENDPOINTS.ROLES_DROPDOWN);
};

export const getRoleById = async (id) => {
  return axiosInstance.get(API_ENDPOINTS.ROLE_BY_ID(id));
};

export const createRole = async (roleData) => {
  return axiosInstance.post(API_ENDPOINTS.ROLE_CREATE, roleData);
};

export const updateRole = async (id, roleData) => {
  return axiosInstance.put(API_ENDPOINTS.ROLE_UPDATE(id), roleData);
};

export const deleteRole = async (id, deleted_by) => {
  return axiosInstance.delete(API_ENDPOINTS.ROLE_DELETE(id), {
    data: { deleted_by },
  });
};

export const getStudy = async (payload) => {
  return axiosInstance.post(API_ENDPOINTS.SURVEY_GET_STUDY, payload, {
    skipAuth: false,
  });
};

export const submitSurvey = async (userId, studyId, siteid, subjectId, responseData) => {
  return axiosInstance.post(
    API_ENDPOINTS.SURVEY_SUBMIT,
    {
      userId,
      studyId,
      siteid,
      subjectId,
      responseData,
    },
    {
      skipAuth: false,
    }
  );
};

export const saveSurveyDraft = async (userId, studyId, siteid, subjectId, responseData) => {
  return axiosInstance.post(
    API_ENDPOINTS.SURVEY_SAVE_DRAFT,
    {
      userId,
      studyId,
      siteid,
      subjectId,
      responseData,
    },
    {
      skipAuth: false,
    }
  );
};

export const getUserSurveyResponses = async (userId, studyId, subjectId = null, siteId = null) => {
  const params = {};
  if (siteId) params.siteid = siteId;
  
  return axiosInstance.get(
    API_ENDPOINTS.SURVEY_USER_RESPONSES(userId, studyId, subjectId),
    {
      params,
      skipAuth: false,
    }
  );
};

export const surveyHealthCheck = async () => {
  return axiosInstance.get(API_ENDPOINTS.SURVEY_HEALTH, {
    skipAuth: true,
  });
};

export const getAuditLogs = async (params) => {
  return axiosInstance.post(API_ENDPOINTS.AUDIT_GET_LOGS, params);
};

export const getAuditDetails = async (auditId) => {
  return axiosInstance.post(API_ENDPOINTS.AUDIT_GET_DETAILS, {
    audit_id: auditId,
  });
};

export const downloadAuditLogs = async (type = 'xlsx') => {
  return axiosInstance.post(
    API_ENDPOINTS.AUDIT_DOWNLOAD,
    { type },
    {
      responseType: 'blob',
    }
  );
};
export const getStudiesStats = async () => {
  return axiosInstance.post(API_ENDPOINTS.STUDIES_STATS);
};
// export const getStudiesStats = async (params = {}) => {
//   console.log('API params being sent:', params);
//   const response = await axiosInstance.post(API_ENDPOINTS.STUDIES_STATS, params);
//   return response.data;
// };
export const getSubjectStats = async (params = {}) => {
  console.log('API params being sent:', params);
  const response = await axiosInstance.post(API_ENDPOINTS.SUBJECT_STATS, params);
  return response.data;
};

export const getRecentSubmissions = async () => {
  return axiosInstance.post(API_ENDPOINTS.STUDIES_RECENT_SUBMISSIONS);
};
export const getStudiesDropdown = async () => {
  return axiosInstance.get(API_ENDPOINTS.STUDIES_DROPDOWN);
};

export const getSitesDropdown = async () => {
  return axiosInstance.get(API_ENDPOINTS.SITES_DROPDOWN);
};
export const checkUserConsent = async () => {
  return axiosInstance.post(API_ENDPOINTS.CHECK_CONSENT);
};
// export const createSubject = async (subjectData) => {
//   try {
//     const response = await axiosInstance.post(API_ENDPOINTS.CREATE_SUBJECT, subjectData);
//     return response.data;
//   } catch (error) {
//     console.error('API Error in createSubject:', error);
//     throw error;
//   }
// };
export const createSubject = async (subjectData) => {
  try {
    console.log('Making API call with:', subjectData);
    const response = await axiosInstance.post(
      API_ENDPOINTS.CREATE_SUBJECT,
      subjectData
    );
    console.log('Response from interceptor:', response);
    return response;
  } catch (error) {
    console.error('API Error in createSubject:', error);
    console.error('Error response:', error.response);
    throw error;
  }
};
export const getSubjects = async () => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.GET_SUBJECTS);
    return response;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};
export const upload = async (formData) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.UPLOAD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};
export const getConsent = async () => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.CONSENT);
    return response;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};
export const createConsent = async (data) => {
  try {
    console.log('Creating consent with data:', data);

    const response = await axiosInstance.post(
      API_ENDPOINTS.CONSENT_CREATE,
      data
    );

    console.log('Create consent response:', response);
    return response;
  } catch (error) {
    console.error('Error creating consent:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};
export const getFiles = async () => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.GET_FILES);
    return response;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};
export const deleteFiles = async (data) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.DELETE, data);
    return response;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};
export const getConsents = async (data) => {
  try {
    const response = await axiosInstance.post(API_ENDPOINTS.GET_CONSENT, data);
    return response;
  } catch (error) {
    console.error('Error fetching consents:', error);
    throw error;
  }
};
export const getConsentsFiles = async (data) => {
  try {
    const response = await axiosInstance.post(
      API_ENDPOINTS.GET_CONSENT_FILES,
      data
    );
    return response;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};
export const updateConsent = async (data) => {
  try {
    const response = await axiosInstance.post(
      API_ENDPOINTS.UPDATDE_STATUS,
      data
    );
    return response;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};
export const consentDropdown = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.USERS_EXPANDED);
    return response;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};
export const deleteSubjects = async (id) => {
  try {
    const response = await axiosInstance.delete(`${API_ENDPOINTS.SUBJECT_DELETE}/${id}`,);
    return response;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};
export const getCreatedSubjects = async () => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.GET_CREATED_SUBJECTS);
    return response;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};
export const getConsentsDownload = async (data) => {
  try {
    const response = await axiosInstance.post(
      API_ENDPOINTS.DOWNLOAD_FORM,
      data,
      {
        responseType: 'blob'
      }
    );
    console.log('Download response in api file:', response?.headers?.['content-disposition'] || 'No content-disposition header');
    return response;
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};
export default {
  axiosInstance,
  API_ENDPOINTS,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  sendOTP,
  verifyOTP,
  resendOTP,
  checkEmail,
  logout,
  getProfile,
  updateProfile,
  getUsersList,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  getUserHistory,
  getRolesList,
  getRolesDropdown,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  getStudy,
  submitSurvey,
  saveSurveyDraft,
  getUserSurveyResponses,
  surveyHealthCheck,
  getStudiesStats,
  getRecentSubmissions,
  getStudiesDropdown,
  getSitesDropdown,
  checkUserConsent,
  upload,
  getConsent,
  createConsent,
  getFiles,
  deleteFiles,
  getConsents,
  getConsentsFiles,
  updateConsent,
  consentDropdown,
  getSubjectStats,
  deleteSubjects,
  getCreatedSubjects,
  getConsentsDownload
};
